import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { noClientsI18n } from '../i18n';
import { getNoClientsProtocolAnalisys } from '../../../services/protocolService';
import '../../Protocols/protocolGeneralDetail.scss';
import usePermissionsAI from 'permissions/modules/artificialIntelligence/usePermissionsAI';

const NoClientsProtocolAnalisys = ({ DesignSystemComponents }) => {
  const { Card, Table, Button, Skeleton } = DesignSystemComponents || {};
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useScopedTranslation(noClientsI18n);

  const recordId = searchParams.get('id');
  const lostSalesAudioId = searchParams.get('lostSalesAudioId');
  const dealName = searchParams.get('dealName') || '--';
  const hubspotOwnerName = searchParams.get('hubspotOwnerName') || '--';

  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { canViewNoClientesCall } = usePermissionsAI();

  const normalizeAnalysis = (payload) => {
    if (!payload) return null;
    if (payload.datos_llamada || payload.evaluacion_protocolo) return payload;
    if (
      payload.data &&
      (payload.data.datos_llamada || payload.data.evaluacion_protocolo)
    ) {
      return payload.data;
    }
    return payload;
  };

  useEffect(() => {
    const controller = new AbortController();

    const load = async () => {
      if (!recordId) {
        setAnalysis(null);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await getNoClientsProtocolAnalisys(recordId);

        setAnalysis(normalizeAnalysis(response));
      } catch (e) {
        console.error('Error loading protocol analysis:', e);
        setError(t('nc_pa_error'));
        setAnalysis(null);
      } finally {
        setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, [recordId]);

  const callData = analysis?.datos_llamada || null;
  const evaluation = Array.isArray(analysis?.evaluacion_protocolo)
    ? analysis.evaluacion_protocolo
    : [];
  const cause = analysis?.analisis_causa_perdida || null;
  const rescue = analysis?.oportunidad_rescate || null;
  const recommendation = analysis?.recomendacion_mejora || null;

  const overallPercent = useMemo(() => {
    if (!evaluation.length) return null;
    const nums = evaluation
      .map((x) => Number(x?.porcentaje_cumplimiento))
      .filter((n) => !Number.isNaN(n));
    if (!nums.length) return null;
    const avg = nums.reduce((a, b) => a + b, 0) / nums.length;
    return Math.round(avg);
  }, [evaluation]);

  const scoreBadge = useMemo(() => {
    const score = Number(overallPercent);
    if (Number.isNaN(score)) return null;
    if (score >= 90) return { label: t('nc_pa_score_excellent'), color: 'success' };
    if (score >= 80) return { label: t('nc_pa_score_good'), color: 'success' };
    if (score >= 70) return { label: t('nc_pa_score_acceptable'), color: 'warning' };
    return { label: t('nc_pa_score_low'), color: 'danger' };
  }, [overallPercent, t]);

  const evaluationColumns = [
    {
      accessor: 'paso',
      label: t('nc_pa_col_step'),
      width: '72px',
      render: (row) => (
        <span className='no-clients-eval-compact-cell'>
          {row?.paso || '--'}
        </span>
      ),
    },
    {
      accessor: 'descripcion_paso_evaluado',
      label: t('nc_pa_col_criteria'),
      render: (row) => row?.descripcion_paso_evaluado || '-',
    },
    {
      accessor: 'estado_cumplimiento',
      label: t('nc_pa_col_status'),
      width: '110px',
      render: (row) => (
        <span className='no-clients-eval-compact-cell'>
          {row?.estado_cumplimiento || '--'}
        </span>
      ),
    },
    {
      accessor: 'porcentaje_cumplimiento',
      label: '%',
      width: '64px',
      render: (row) => {
        const n = Number(row?.porcentaje_cumplimiento);
        return (
          <span className='no-clients-eval-compact-cell'>
            {Number.isNaN(n) ? '--' : `${Math.floor(n)}%`}
          </span>
        );
      },
    },
    {
      accessor: 'justificacion',
      label: t('nc_pa_col_justification'),
      render: (row) => row?.justificacion || '-',
    },
  ];

  const handleViewTranscript = () => {
    if (!recordId) return;
    navigate(`/ia/no-clientes/analisis-protocolo/transcripcion/${recordId}`);
  };

  if (!Card || !Table) return null;

  return (
    <div className='protocols-page'>
      <div className='d-flex align-items-center gap-2 mb-2'>
        {Button ? (
          <Button
            variant='naked'
            iconStart='chevronLeft'
            label={t('nc_back')}
            size='sm'
            onClick={() => navigate(-1)}
          />
        ) : (
          <button type='button' onClick={() => navigate(-1)}>
            {t('nc_back')}
          </button>
        )}
      </div>

      <div className='mb-4'>
        <h2 className='fw-semibold'>{t('nc_pa_title')}</h2>
        <h4 className='fw-normal'>{t('nc_pa_subtitle')}</h4>
      </div>

      {error && <p className='text-danger'>{error}</p>}

      <Card specialPadding='px-4 pt-4 pb-4' className='mb-4'>
        <div className='protocol-general-summary'>
          <div>
            <h3>{t('nc_pa_recommendation')}</h3>
            <p>
              {loading
                ? t('nc_pa_loading')
                : recommendation || t('nc_pa_notAvailable')}
            </p>
          </div>
          <div>
            <div className='protocol-score-circle'>
              {overallPercent === null ? '--' : `${overallPercent}%`}
            </div>
            {scoreBadge !== null && overallPercent !== null ? (
              <span className={`badge bg-${scoreBadge.color}`}>
                {scoreBadge.label}
              </span>
            ) : (
              <Skeleton />
            )}
          </div>
        </div>
      </Card>

      <Card specialPadding='px-4 pt-4 pb-4' className='mb-4'>
        <h3>{t('nc_pa_callData')}</h3>

        {callData ? (
          <>
            <div className='row'>
              <div className='col'>
                <strong>{t('nc_pa_client')}</strong> {dealName}
              </div>
              <div className='col'>
                <strong>{t('nc_pa_consultant')}</strong> {hubspotOwnerName}
              </div>
              <div className='col'>
                <strong>{t('nc_pa_service')}</strong> {callData.servicio || '--'}
              </div>
            </div>

            <div className='mt-3'>
              {Button ? (
                <Button
                  variant='primary'
                  label={t('nc_pa_viewTranscript')}
                  size='sm'
                  onClick={handleViewTranscript}
                  disabled={!canViewNoClientesCall}
                />
              ) : (
                <button
                  type='button'
                  className='btn btn-primary btn-sm'
                  onClick={handleViewTranscript}
                  disabled={!canViewNoClientesCall}
                >
                  {t('nc_pa_viewTranscript')}
                </button>
              )}
            </div>
          </>
        ) : (
          <p>{t('nc_pa_noData')}</p>
        )}
      </Card>

      <Card specialPadding='px-4 pt-4 pb-2' className='mb-4'>
        <h3>{t('nc_pa_evaluation')}</h3>
        <div className='no-clients-eval-table'>
          <Table
            columns={evaluationColumns}
            data={evaluation}
            loading={loading}
          />
        </div>
      </Card>

      <div className='row'>
        <div className='col'>
          <Card specialPadding='px-4 pt-4 pb-4'>
            <h3>{t('nc_pa_cause')}</h3>
            {cause ? (
              <>
                <p>
                  <strong>{t('nc_pa_causeType')}</strong> {cause.tipo_causa || '--'}
                </p>
                <p>
                  <strong>{t('nc_pa_causeSpecific')}</strong> {cause.causal_especifica || '--'}
                </p>
                <p>
                  <strong>{t('nc_pa_crmInconsistency')}</strong>{' '}
                  {cause.inconsistencia_crm ? t('nc_pa_yes') : t('nc_pa_no')}
                </p>
                <p>{cause.detalle_inconsistencia}</p>
              </>
            ) : (
              <p>{t('nc_pa_notAvailable')}</p>
            )}
          </Card>
        </div>

        <div className='col'>
          <Card specialPadding='px-4 pt-4 pb-4'>
            <h3>{t('nc_pa_rescue')}</h3>
            {rescue ? (
              <>
                <p>
                  <strong>{t('nc_pa_rescueApplies')}</strong>{' '}
                  {rescue.aplica_rescate ? t('nc_pa_yes') : t('nc_pa_no')}
                </p>
                <p>
                  <strong>{t('nc_pa_rescueProposal')}</strong> {rescue.propuesta_viable}
                </p>
                <p>{rescue.justificacion}</p>
              </>
            ) : (
              <p>{t('nc_pa_notAvailable')}</p>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default NoClientsProtocolAnalisys;
